import {
  Flex,
  VStack,
  Text,
  Box,
  Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import { useState } from "react";
import { ChevronRight, ChevronLeft } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { NavigateFunction } from "react-router-dom";
import type { MenuItem } from "../../../config/menu";

const ACCENT = "#009EFB";

interface HoverMenuCardProps {
  title: string;
  description: string;
  items: MenuItem[];
  navigate: NavigateFunction;
  icon: LucideIcon;
  color?: string;
}

export const HoverMenuCard = ({
  title,
  description,
  items,
  navigate,
  icon,
  color = "ggBlue.300",
}: HoverMenuCardProps) => {
  const [currentItems, setCurrentItems] = useState<MenuItem[]>(items);
  const [breadcrumb, setBreadcrumb] = useState<string[]>([title]);

  const IconComp = icon;

  const cardBg = useColorModeValue("white", "#0f1f3a");
  const cardBorder = useColorModeValue("rgba(0, 0, 0, 0.06)", "rgba(255, 255, 255, 0.08)");
  const titleColor = useColorModeValue("#0a0a0a", "#fff");
  const descColor = useColorModeValue("#4d525c", "#98a2b3");
  const iconChipBg = useColorModeValue("rgba(0, 158, 251, 0.1)", "rgba(0, 158, 251, 0.16)");
  const cardShadow = useColorModeValue("0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -12px rgba(0,0,0,0.12)", "0 1px 2px rgba(0,0,0,0.4), 0 8px 24px -12px rgba(0,0,0,0.6)");
  const cardShadowHover = useColorModeValue("0 1px 2px rgba(0,0,0,0.04), 0 16px 36px -12px rgba(0,0,0,0.18)", "0 1px 2px rgba(0,0,0,0.4), 0 16px 36px -12px rgba(0,0,0,0.8)");

  const handleItemClick = (item: MenuItem) => {
    if (item.subitems && item.subitems.length > 0) {
      setCurrentItems(item.subitems);
      setBreadcrumb([...breadcrumb, item.name]);
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const handleBack = () => {
    if (breadcrumb.length > 1) {
      const newBreadcrumb = breadcrumb.slice(0, -1);
      setBreadcrumb(newBreadcrumb);
      let targetItems = items;
      for (let i = 1; i < newBreadcrumb.length; i++) {
        const found = targetItems.find((item) => item.name === newBreadcrumb[i]);
        if (found?.subitems) targetItems = found.subitems;
      }
      setCurrentItems(targetItems);
    }
  };

  const resetNavigation = () => {
    setCurrentItems(items);
    setBreadcrumb([title]);
  };

  return (
    <Box
      position="relative"
      w="100%"
      minH={{ base: "220px", md: "240px" }}
      bg={cardBg}
      border="1px solid"
      borderColor={cardBorder}
      borderRadius="20px"
      boxShadow={cardShadow}
      overflow="hidden"
      role="group"
      cursor="pointer"
      transition="all 0.2s"
      _hover={{ boxShadow: cardShadowHover, transform: "translateY(-2px)" }}
      onMouseLeave={resetNavigation}
    >
      <Flex direction="column" h="100%" px={7} py={6}>
        <Flex align="center" gap={3} mb={3}>
          {IconComp && (
            <Flex align="center" justify="center" w="44px" h="44px" borderRadius="12px" bg={iconChipBg} flexShrink={0}>
              <IconComp size={20} strokeWidth={1.75} color={ACCENT} />
            </Flex>
          )}
          <Text fontSize="lg" fontWeight="600" letterSpacing="-0.02em" color={titleColor}>{title}</Text>
        </Flex>
        <Text fontSize="sm" color={descColor} lineHeight="1.55" letterSpacing="-0.01em">{description}</Text>
      </Flex>

      <Box
        position="absolute"
        bottom={-2.5}
        left={0}
        right={0}
        h="0%"
        bg={color}
        color="white"
        transition="all 0.35s ease"
        _groupHover={{ h: "105%" }}
        p={3}
        overflowY="auto"
        pb={1}
        css={{ "&::-webkit-scrollbar": { display: "none" }, msOverflowStyle: "none", scrollbarWidth: "none" }}
      >
        <VStack align="start" spacing={2} h="100%">
          <Flex align="center" justify="space-between" w="100%" mb={2}>
            <Flex align="center" gap={2}>
              {breadcrumb.length > 1 && (
                <Icon as={ChevronLeft} boxSize={6} cursor="pointer" onClick={(e) => { e.stopPropagation(); handleBack(); }} _hover={{ transform: "scale(1.2)" }} />
              )}
              <Text fontSize="2xl" fontWeight="600" letterSpacing="-0.02em" color="white">
                {breadcrumb[breadcrumb.length - 1]}
              </Text>
            </Flex>
          </Flex>

          <VStack align="start" spacing={1.5} w="100%" flex={1}>
            {currentItems.map((item, index) => (
              <Flex
                key={item.path || `${item.name}-${index}`}
                align="center"
                gap={2.5}
                w="100%"
                cursor="pointer"
                _hover={{ fontWeight: "600", transform: "translateX(4px)" }}
                transition="all 0.2s"
                onClick={(e) => { e.stopPropagation(); handleItemClick(item); }}
                pl={2}
              >
                <Box w="5px" h="5px" borderRadius="full" bg="whiteAlpha.800" flexShrink={0} />
                <Text fontSize="sm" letterSpacing="-0.01em" color="white">{item.name}</Text>
                {item.subitems && item.subitems.length > 0 && <Icon as={ChevronRight} boxSize={2} />}
              </Flex>
            ))}
          </VStack>
        </VStack>
      </Box>
    </Box>
  );
};
